import type { Announcement } from '@clockwise/clients/announcements';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ANNOUNCEMENT_CRITERIA_COLOR_CLASSES } from '@/components/custom/announcement-criteria-badge';
import { MarkdownContent } from '@/components/custom/markdown-content';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { announcementsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

const BANNER_HEIGHT_PX = 44;
const ROTATE_INTERVAL_MS = 18_000;
// Short poll, not a one-shot load: a scheduled banner can go live or expire
// while a tab stays open for hours, and this is a cheap tenant-scoped GET.
const POLL_INTERVAL_MS = 60_000;
const DISMISSED_STORAGE_KEY = 'clockwise.dismissed-announcements';

function readDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    // Private browsing / blocked storage — treat as "nothing dismissed yet"
    // rather than breaking the banner.
    return new Set();
  }
}

function writeDismissed(ids: Set<string>): void {
  try {
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // Best-effort only — see readDismissed.
  }
}

/**
 * Site-wide banner, rendered above the app shell for every signed-in user.
 * `position: fixed` (same technique as `ImpersonationBanner`) so toggling it
 * doesn't reflow the page; `onVisibilityChange` tells `AppLayout` whether to
 * reserve `NOTIFICATION_BANNER_HEIGHT` of top padding (nothing showing →
 * no dead space).
 *
 * Dismissal (Warning/Information only — Error is never dismissable, per the
 * admin's "criteria" choice) is per-browser via localStorage, not a backend
 * table: there's no product need for a dismissal to follow a user across
 * devices, and it keeps this a pure read + local-storage feature.
 *
 * The strip is a fixed height (AppLayout reserves exactly that much), so the
 * heading + subheading stay on ONE line and ellipsize rather than wrap — a
 * 200-char heading used to wrap to 3 lines and spill above and below the
 * strip. The full text is on hover (`title`) and in the Learn more sheet,
 * which is offered whenever the text is cut off, explanation or not.
 */
export function NotificationBanner({
  offsetTop = 0,
  onVisibilityChange,
}: {
  offsetTop?: number;
  onVisibilityChange?: (visible: boolean) => void;
}) {
  const query = useQuery({
    queryKey: queryKeys.announcements.live(),
    queryFn: () => announcementsApi.live(),
    refetchInterval: POLL_INTERVAL_MS,
  });

  const [dismissed, setDismissed] = useState<Set<string>>(() => readDismissed());
  const [index, setIndex] = useState(0);
  const [learnMoreOpen, setLearnMoreOpen] = useState(false);
  // The announcement the Learn more sheet was opened for — a snapshot, so the
  // sheet keeps showing it while the strip changes underneath (a poll can
  // drop it). Kept after close so the sheet's exit animation has content.
  const [learnMore, setLearnMore] = useState<Announcement | null>(null);
  const [truncated, setTruncated] = useState(false);
  const headingRef = useRef<HTMLSpanElement>(null);
  const subheadingRef = useRef<HTMLSpanElement>(null);

  const live = query.data?.data ?? [];
  // Re-filtering on every render was previously fine (a handful of rows) but
  // also reran on every 18s rotation tick for no reason — memoize so it only
  // recomputes when the data or dismissal set actually changes.
  const visible = useMemo(
    () => live.filter((a) => a.criteria === 'error' || !dismissed.has(a.id)),
    [live, dismissed],
  );

  // Paused while Learn more is open, so the strip still matches the sheet
  // when it closes; reopening restarts a full interval.
  useEffect(() => {
    if (visible.length <= 1 || learnMoreOpen) return;
    const handle = setInterval(() => {
      setIndex((i) => (i + 1) % visible.length);
    }, ROTATE_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [visible.length, learnMoreOpen]);

  useEffect(() => {
    onVisibilityChange?.(visible.length > 0);
  }, [visible.length, onVisibilityChange]);

  // Modulo, not a raw index read: `visible` can shrink between polls (a
  // banner expires or gets dismissed) while `index` still points past its
  // new end — `visible[index]` would then be `undefined` and crash the
  // whole app shell (this is mounted globally in AppLayout). Modulo keeps
  // `index` valid for whatever `visible.length` is on THIS render, with no
  // separate clamping effect needed.
  const current: Announcement | undefined = visible[index % visible.length];

  // Re-measured on every rotation and on resize: whether the one-line text
  // is cut off depends on both the content and the viewport width.
  useLayoutEffect(() => {
    const measure = () => {
      const isCut = (el: HTMLElement | null) => el !== null && el.scrollWidth > el.clientWidth;
      setTruncated(isCut(headingRef.current) || isCut(subheadingRef.current));
    };
    measure();
    const el = headingRef.current?.parentElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [current?.id, current?.heading, current?.subheading]);

  const learnMoreSheet = learnMore ? (
    <Sheet open={learnMoreOpen} onOpenChange={setLearnMoreOpen}>
      <SheetContent className="overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{learnMore.heading}</SheetTitle>
          {learnMore.subheading ? (
            <SheetDescription>{learnMore.subheading}</SheetDescription>
          ) : null}
        </SheetHeader>
        {learnMore.explanation ? (
          <MarkdownContent className="px-4">{learnMore.explanation}</MarkdownContent>
        ) : null}
      </SheetContent>
    </Sheet>
  ) : null;

  // The sheet outlives the strip: if every banner goes away mid-read (expired
  // between polls), the reader keeps what they opened until they close it.
  if (!current) return learnMoreSheet;
  const fullText = [current.heading, current.subheading].filter(Boolean).join(' — ');

  const dismiss = (id: string) => {
    const next = new Set(dismissed);
    next.add(id);
    setDismissed(next);
    writeDismissed(next);
  };

  return (
    <>
      <div
        role="status"
        style={{ height: BANNER_HEIGHT_PX, top: offsetTop }}
        className={`fixed inset-x-0 z-50 flex flex-col overflow-hidden items-center justify-center gap-0.5 px-4 py-1 text-sm ${ANNOUNCEMENT_CRITERIA_COLOR_CLASSES[current.criteria]}`}
      >
        <div className="flex w-full items-center justify-between gap-3">
          {/* Heading keeps priority: it never shrinks below its text except
              to fit the row (`max-w-full`); the subheading takes what's left. */}
          <div className="flex min-w-0 flex-1 items-baseline text-left" title={fullText}>
            <span ref={headingRef} className="max-w-full shrink-0 truncate font-semibold">
              {current.heading}
            </span>
            {current.subheading ? (
              <span ref={subheadingRef} className="ml-2 min-w-0 flex-1 truncate opacity-90">
                {current.subheading}
              </span>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {current.explanation || truncated ? (
              <Button
                size="xs"
                variant="outline"
                className="shrink-0 border-current/50 bg-transparent text-current shadow-none hover:bg-black/10 hover:text-current"
                onClick={() => {
                  setLearnMore(current);
                  setLearnMoreOpen(true);
                }}
              >
                Learn more
              </Button>
            ) : null}
            {current.criteria !== 'error' ? (
              <button
                type="button"
                aria-label="Dismiss"
                className="shrink-0 rounded p-1 hover:bg-black/10"
                onClick={() => dismiss(current.id)}
              >
                <X className="size-4" />
              </button>
            ) : null}
          </div>
        </div>
        {visible.length > 1 ? (
          <div className="flex items-center gap-1">
            {visible.map((a, i) => (
              <span
                key={a.id}
                className={`size-1.5 rounded-full ${i === index ? 'bg-current' : 'bg-current/30'}`}
              />
            ))}
          </div>
        ) : null}
      </div>

      {learnMoreSheet}
    </>
  );
}

export const NOTIFICATION_BANNER_HEIGHT = BANNER_HEIGHT_PX;
